/*
 * Nische hero slideshow.
 *
 * Swiping is native scroll-snap, so there is no gesture JS to get wrong. This
 * only handles autoplay, arrows, dots and - the part that matters for
 * performance - video loading.
 *
 * Videos ship with no src at all. A source is attached the first time its slide
 * becomes active while the hero is on screen, so a video hero adds nothing to
 * page load. Anything off screen or off slide is paused.
 */
class NischeHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (!this.track || this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.index = 0;
    this.inView = false;
    this.paused = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot)));
    });

    // Keep state in step with the real scroll position, however it was moved -
    // but ignore the events our own navigation generates, or the mid-flight
    // position gets read back as "still on the previous slide".
    this.track.addEventListener('scroll', () => {
      if (this.navigating) return;
      clearTimeout(this._scrollEnd);
      this._scrollEnd = setTimeout(() => this.syncFromScroll(), 90);
    }, { passive: true });

    // Only run while the hero is actually visible.
    this.observer = new IntersectionObserver(
      (entries) => {
        this.inView = entries[0].isIntersecting;
        this.inView ? this.play() : this.stop();
        this.updateMedia();
      },
      { threshold: 0.15 }
    );
    this.observer.observe(this);

    // Autoplay should never fight the user.
    ['mouseenter', 'focusin', 'touchstart'].forEach((evt) =>
      this.addEventListener(evt, () => this.hold(true), { passive: true })
    );
    ['mouseleave', 'focusout', 'touchend', 'touchcancel'].forEach((evt) =>
      this.addEventListener(evt, () => this.hold(false), { passive: true })
    );
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stop() : this.play();
    });

    this.updateMedia();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  get autoplayOn() {
    return (
      this.dataset.autoplay === 'true' &&
      this.slides.length > 1 &&
      !this.reduceMotion
    );
  }

  hold(state) {
    this.paused = state;
    state ? this.stop() : this.play();
  }

  play() {
    this.stop();
    if (!this.autoplayOn || this.paused || !this.inView) return;
    const speed = Number(this.dataset.speed) || 6000;
    this.timer = setInterval(() => this.step(1), speed);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  step(dir) {
    this.goTo((this.index + dir + this.slides.length) % this.slides.length);
  }

  /* Mandatory scroll-snap fights a programmatic smooth scroll: it re-snaps to
     the nearest point while the animation is still near the old slide, so the
     track springs back to where it started. Suspending snap for the duration of
     our own scroll, then restoring it, makes navigation deterministic while
     leaving native touch swiping untouched. */
  goTo(i) {
    this.index = i;
    this.navigating = true;
    this.track.style.scrollSnapType = 'none';
    this.track.scrollTo({
      left: this.slides[i].offsetLeft,
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });

    clearTimeout(this._settle);
    this._settle = setTimeout(() => {
      this.track.style.scrollSnapType = '';
      this.navigating = false;
    }, this.reduceMotion ? 60 : 600);

    this.setActive(i);
  }

  syncFromScroll() {
    const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (i !== this.index && this.slides[i]) {
      this.index = i;
      this.setActive(i);
    }
  }

  setActive(i) {
    this.dots.forEach((dot, n) => {
      dot.classList.toggle('is-active', n === i);
      dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
    });
    this.updateMedia();
  }

  /* Attach a source only once, and only for the video that is actually on
     screen at this breakpoint - the other breakpoint's element is display:none
     and must never be given a src. */
  updateMedia() {
    this.slides.forEach((slide, n) => {
      slide.querySelectorAll('[data-video]').forEach((video) => {
        const active = n === this.index && this.inView;
        const visible = video.offsetParent !== null;

        if (active && visible) {
          if (!video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.load();
          }
          const attempt = video.play();
          if (attempt) attempt.catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    });
  }
}

if (!customElements.get('nische-hero')) {
  customElements.define('nische-hero', NischeHero);
}
